export const ACTIVE_SCOPE_SESSION_STORAGE_KEY = 'active-scope';
export const DEFAULT_ACTIVE_SCOPE = 'pai-scope-root';

const listeners = new Set<() => void>();
const scopeChangeHandlers = new Set<(nextScope: string, previousScope: string) => Promise<void> | void>();
const isSessionStorageAvailable = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const normalizeActiveScope = (scope: unknown): string | null => {
  if (typeof scope !== 'string') {
    return null;
  }

  const trimmedScope = scope.trim();
  return trimmedScope.length > 0 ? trimmedScope : null;
};

// Best-effort persistence: storage failures should not block UI state updates.
const persistActiveScope = (scope: string) => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    window.sessionStorage.setItem(ACTIVE_SCOPE_SESSION_STORAGE_KEY, scope);
  } catch {
    // Ignore sessionStorage write errors (e.g. private mode quota restrictions).
  }
};

// Reads the persisted scope and normalizes it. Returns null if missing/invalid.
const getStoredScope = () => {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  try {
    return normalizeActiveScope(window.sessionStorage.getItem(ACTIVE_SCOPE_SESSION_STORAGE_KEY));
  } catch {
    // Ignore sessionStorage read errors and fallback to default scope.
    return null;
  }
};

// Initial snapshot: prefer persisted value, fallback to app default.
const getDefaultScopeSnapshot = () => getStoredScope() ?? DEFAULT_ACTIVE_SCOPE;

let activeScope = getDefaultScopeSnapshot();
let activeScopeChangePromise = Promise.resolve();
persistActiveScope(activeScope);

const notifyListeners = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

export const getActiveScope = () => activeScope;

const executeScopeChangeHandlers = async (nextScope: string, previousScope: string) => {
  await Promise.allSettled(Array.from(scopeChangeHandlers).map((handler) => handler(nextScope, previousScope)));
};

/**
 * Update flow:
 * 1) normalize input + persist
 * 2) short-circuit if unchanged
 * 3) update in-memory snapshot
 * 4) notify useSyncExternalStore listeners (triggers React re-check)
 * 5) run async side-effects in sequence via activeScopeChangePromise
 */
export const setActiveScope = (nextScope: string | null | undefined) => {
  const normalizedScope = normalizeActiveScope(nextScope) ?? DEFAULT_ACTIVE_SCOPE;
  const previousScope = activeScope;

  persistActiveScope(normalizedScope);
  if (normalizedScope === previousScope) {
    return activeScopeChangePromise;
  }

  activeScope = normalizedScope;
  notifyListeners();
  activeScopeChangePromise = activeScopeChangePromise.then(() =>
    executeScopeChangeHandlers(normalizedScope, previousScope),
  );

  return activeScopeChangePromise;
};

// useSyncExternalStore-compatible subscribe function.
export const subscribeToActiveScope = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Register app side-effects that should run after scope changes.
export const registerActiveScopeChangeHandler = (
  handler: (nextScope: string, previousScope: string) => Promise<void> | void,
) => {
  scopeChangeHandlers.add(handler);

  return () => {
    scopeChangeHandlers.delete(handler);
  };
};
